import React from 'react';
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import InputAdornment from "@mui/material/InputAdornment";
import Text from '../../components/Text.js';
import TextInput from '../../components/TextFields/TextInput.js';
import DateInput from '../../components/TextFields/DateInput.js';
import Checkbox from '../../components/Checkbox.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import colors from '../../theme/colors.js';

/**
 * A React component for password confirmation and beneficiary changes.
 * This component includes input fields for confirming password, user's name, signature, and date.
 * It also provides a checkbox for acknowledging information accuracy.
 * @param {object} props - The properties passed to the component.
 * @param {function} props.handleInputChange - Function to handle input changes.
 * @param {object} props.signage - Object containing information for password confirmation and beneficiary changes.
 * @param {string} props.signage.password - Password input value.
 * @param {boolean} props.signage.passwordVisible - Flag to toggle password visibility.
 * @param {string} props.signage.name - User's name input value.
 * @param {string} props.signage.signature - Signature input value.
 * @param {string} props.signage.datedOn - Date input value.
 * @param {boolean} props.signage.acceptedTerms - Flag indicating if terms have been accepted.
 * @param {object} props.signageErrorState - Object containing error states for signage inputs.
 * @param {string} props.signageErrorState.passwordError - Error message for password input.
 * @param {boolean} props.signageErrorState.signageNameError - Error state for name input.
 * @param {boolean} props.signageErrorState.signatureError - Error state for signature input.
 * @param {boolean} props.signageErrorState.signageDatedOnError - Error state for date input.
 * @returns {JSX.Element} - The PasswordConfirmation component JSX.
 */
const PasswordConfirmation = ({
  handleInputChange,
  signage,
  signageErrorState,
  title
}) => {
  const {
    password,
    passwordVisible,
    name,
    signature,
    datedOn,
    acceptedTerms
  } = signage;
  const {
    passwordError,
    signageNameError,
    signatureError,
    signageDatedOnError
  } = signageErrorState;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    align: "left",
    medium: true,
    display: "inline-block"
  }, title), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.password),
    value: password,
    inputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end"
      }, passwordVisible ? /*#__PURE__*/React.createElement(VisibilityIcon, {
        style: {
          cursor: "pointer",
          color: colors.darkGrey,
          marginRight: "8px"
        },
        onClick: () => handleInputChange("passwordVisible", !passwordVisible)
      }) : /*#__PURE__*/React.createElement(VisibilityOffIcon, {
        style: {
          cursor: "pointer",
          color: colors.darkGrey,
          marginRight: "8px"
        },
        onClick: () => handleInputChange("passwordVisible", !passwordVisible)
      }))
    },
    onChange: e => handleInputChange('password', e.target.value),
    error: Boolean(passwordError),
    helperText: passwordError,
    secure: !passwordVisible,
    fullWidth: true,
    autofocus: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.yourName),
    fullWidth: true,
    value: name,
    onChange: e => handleInputChange('name', e.target.value),
    error: signageNameError
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.signature),
    fullWidth: true,
    value: signature,
    onChange: e => handleInputChange('signature', e.target.value),
    isSignature: true,
    error: signatureError
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.account.datedOnYYYYMMDD),
    fullWidth: true,
    value: datedOn,
    onChange: e => handleInputChange('datedOn', e.target.value),
    error: signageDatedOnError
  }), /*#__PURE__*/React.createElement(FormGroup, {
    row: true,
    style: {
      marginTop: 20
    }
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: acceptedTerms,
      onChange: () => handleInputChange('acceptedTerms', !acceptedTerms),
      value: "acceptedTerms",
      color: "secondary"
    }),
    label: /*#__PURE__*/React.createElement(Text, {
      avMedium: true
    }, i18n.t(tokens.labels.account.acknowledgeInfoAccuracy))
  })));
};
export default PasswordConfirmation;